/**
 * Formatting utilities for the dashboard
 */

/**
 * Format a number as currency
 * @param {number} value - The value to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, currency = 'USD') {
  if (value == null) return '—';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a unit cost value - shows 2 decimal places for values under $100
 * @param {number} value - The value to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export function formatUnitCost(value, currency = 'USD') {
  if (value == null) return '—';

  const decimals = Math.abs(value) < 100 ? 2 : 0;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a unit cost with awareness of the line item's unit. Percentage units
 * ("%", "PCT") render as "3.00%" without a currency prefix; rate-per-thousand
 * units ("$/M") render as "$2.01/M". Everything else falls back to
 * formatUnitCost (currency-formatted with $).
 *
 * @param {number} value - The unit_cost value
 * @param {string} unit - The line item's unit
 * @returns {string} Formatted unit cost string
 */
export function formatUnitCostWithUnit(value, unit) {
  if (value == null) return '—';
  const u = (unit || '').toUpperCase().trim();
  if (u === '%' || u === 'PCT') {
    return `${Number(value).toFixed(2)}%`;
  }
  if (u === '$/M' || u === '$/1000') {
    return `$${Number(value).toFixed(2)}/M`;
  }
  return formatUnitCost(value);
}

/**
 * Format a large currency value in compact form (e.g., $4.5M)
 * @param {number} value - The value to format
 * @returns {string} Formatted compact currency string
 */
export function formatCurrencyCompact(value) {
  if (value == null) return '—';

  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value);
}

/**
 * Format a number with commas
 * @param {number} value - The value to format
 * @returns {string} Formatted number string
 */
export function formatNumber(value) {
  if (value == null) return '—';

  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * Format square footage
 * @param {number} sqft - Square footage value
 * @returns {string} Formatted square footage string
 */
export function formatSqFt(sqft) {
  if (sqft == null) return '—';

  return `${formatNumber(sqft)} sq ft`;
}

/**
 * Format a percentage
 * @param {number} value - The value (0-100 or 0-1 based on isDecimal)
 * @param {boolean} isDecimal - Whether the value is a decimal (0-1)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export function formatPercent(value, isDecimal = false, decimals = 0) {
  if (value == null) return '—';

  const percentValue = isDecimal ? value * 100 : value;
  return `${percentValue.toFixed(decimals)}%`;
}

/**
 * Format a percentage with *at most* `maxDecimals` decimal places, trimming
 * any trailing zeros (e.g. 10 -> "10%", 3.3333 -> "3.33%", 12.5 -> "12.5%").
 * Accepts numbers or numeric strings.
 * @param {number|string} value - The percentage value (already 0-100)
 * @param {number} maxDecimals - Maximum decimal places to show (default 2)
 * @returns {string} Formatted percentage string, or '' for null/blank/NaN
 */
export function formatPercentTrim(value, maxDecimals = 2) {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  return `${Number(num.toFixed(maxDecimals))}%`;
}

/**
 * Format a date relative to today
 * @param {string|Date} date - The date to format
 * @returns {string} Relative date string
 */
export function formatRelativeDate(date) {
  if (!date) return '—';

  const now = new Date();
  const dateObj = new Date(date);
  const diffTime = now - dateObj;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/**
 * Format a date in short format
 * @param {string|Date} date - The date to format
 * @returns {string} Short date string (e.g., "Dec 15, 2024")
 */
export function formatDate(date) {
  if (!date) return '—';

  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format the AI adjustment percentage
 * @param {number} aiPrice - AI suggested price
 * @param {number} finalPrice - Final price
 * @returns {object} Object with value, formatted string, and direction
 */
export function formatAdjustment(aiPrice, finalPrice) {
  if (aiPrice == null || finalPrice == null) {
    return { value: null, formatted: '—', direction: 'neutral' };
  }

  const diff = ((finalPrice - aiPrice) / aiPrice) * 100;
  const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral';

  return {
    value: diff,
    formatted: `${diff > 0 ? '+' : ''}${diff.toFixed(1)}%`,
    direction,
  };
}

/**
 * Format confidence score as a human-readable string
 * @param {number} score - Confidence score (0-1)
 * @returns {object} Object with level, label, and color
 */
export function formatConfidence(score) {
  if (score == null) {
    return { level: 'unknown', label: 'Unknown', color: 'gray' };
  }

  if (score >= 0.85) {
    return { level: 'high', label: 'High Confidence', color: 'green' };
  }
  if (score >= 0.70) {
    return { level: 'medium', label: 'Medium Confidence', color: 'yellow' };
  }
  return { level: 'low', label: 'Low Confidence', color: 'red' };
}
